/*
 * How the rounds have actually been going.
 *
 * This is the one screen here that looks backwards. It exists to answer the questions a manager
 * gets asked and currently has to guess at: how long does the morning round take, is it starting
 * when it is supposed to, and is it running past the end of its window.
 *
 * It is about the round, not the person doing it. A round that has crept from 50 minutes to 95 is
 * a rota problem. Turning it into a performance problem would push staff into recording doses
 * before they give them, which is the one thing this system exists to stop — so there is no league
 * table of staff here, only how many people were recording.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from './api.ts';

type RoundSummary = {
  roundId: string;
  name: string;
  dueAt: string;
  days: number;
  typicalMinutes: number;
  longestMinutes: number;
  longestOn: string;
  typicalDoses: number;
  typicalStart: string;
  overranDays: number;
  lateDoses: number;
  doses: number;
};

type RoundDay = {
  date: string;
  roundId: string;
  name: string;
  startsAt: string;
  dueAt: string;
  endsAt: string;
  firstAt: string;
  lastAt: string;
  minutes: number;
  doses: number;
  staff: number;
  late: number;
  overran: boolean;
  startedLate: number;
};

type Analytics = {
  days: number;
  rounds: RoundSummary[];
  recent: RoundDay[];
  punctuality: Array<{ date: string; onTime: number; late: number; notGiven: number }>;
  totals: { onTime: number; late: number; notGiven: number; recorded: number; onTimePercent: number | null };
  reasons: Array<{ code: string; count: number; label: string }>;
};

export function AnalyticsScreen({ homeId }: { homeId: string }) {
  const [days, setDays] = useState(28);
  const [data, setData] = useState<Analytics | null>(null);
  const [showRound, setShowRound] = useState<string>('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const result = await api.get<Analytics>(`/api/homes/${homeId}/analytics?days=${days}`);
      setData(result);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not work that out.');
    }
  }, [homeId, days]);

  useEffect(() => {
    void load();
  }, [load]);

  const recent = (data?.recent ?? []).filter((day) => !showRound || day.roundId === showRound);

  return (
    <section>
      <div className="head">
        <h1>How the rounds are going</h1>
        <div className="row-actions">
          {[7, 28, 90].map((choice) => (
            <button
              key={choice}
              type="button"
              className={days === choice ? '' : 'quiet'}
              onClick={() => setDays(choice)}
            >
              {choice === 7 ? 'Last week' : choice === 28 ? 'Last 4 weeks' : 'Last 3 months'}
            </button>
          ))}
        </div>
      </div>
      <p className="muted">
        How long each round takes, when it really starts, and how often it runs past the end of its window.
        It counts how many people were recording, not who they were — this is about the rota, not about anybody's
        speed.
      </p>

      {error ? <p className="error" role="alert">{error}</p> : null}
      {data && !data.rounds.length ? <p className="notice">Nothing recorded in this period yet.</p> : null}

      {data?.totals.recorded ? (
        <div className="live-summary">
          <div className="stat"><b>{data.totals.recorded}</b><span>Doses recorded</span></div>
          <div className="stat"><b>{data.totals.onTimePercent}%</b><span>Given inside the round</span></div>
          <div className="stat"><b>{data.totals.late}</b><span>Given after it closed</span></div>
          <div className="stat"><b>{data.totals.notGiven}</b><span>Not given</span></div>
        </div>
      ) : null}

      {(data?.rounds ?? []).map((round) => (
        <article key={round.roundId} className="card">
          <div className="head">
            <div>
              <h2>{round.name}</h2>
              <p className="muted small">Due {round.dueAt} · {round.days} day{round.days === 1 ? '' : 's'} recorded</p>
            </div>
            <button
              type="button"
              className="quiet"
              onClick={() => setShowRound(showRound === round.roundId ? '' : round.roundId)}
            >
              {showRound === round.roundId ? 'Hide the days' : 'Day by day'}
            </button>
          </div>

          <dl className="facts">
            <div>
              <dt>Usually takes</dt>
              <dd>
                {describeMinutes(round.typicalMinutes)}
                <span className="muted small"> · {round.typicalDoses} dose{round.typicalDoses === 1 ? '' : 's'}</span>
              </dd>
            </div>
            <div>
              <dt>Usually starts</dt>
              <dd>{round.typicalStart || '—'}</dd>
            </div>
            <div>
              <dt>Longest</dt>
              <dd>
                {describeMinutes(round.longestMinutes)}
                <span className="muted small"> · {formatDate(round.longestOn)}</span>
              </dd>
            </div>
            <div>
              <dt>Ran past the end</dt>
              <dd className={round.overranDays ? 'warn-text' : ''}>
                {round.overranDays ? `${round.overranDays} of ${round.days} days` : 'Never'}
              </dd>
            </div>
            <div>
              <dt>Recorded after it closed</dt>
              <dd className={round.lateDoses ? 'warn-text' : ''}>
                {round.lateDoses ? `${round.lateDoses} dose${round.lateDoses === 1 ? '' : 's'}` : 'None'}
              </dd>
            </div>
          </dl>

          {showRound === round.roundId ? (
            <div className="scroll">
              <table className="register">
                <thead>
                  <tr>
                    <th>Day</th><th>Started</th><th>Finished</th><th className="num">Took</th>
                    <th className="num">Doses</th><th className="num">Staff</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((day) => (
                    <tr key={`${day.date}-${day.roundId}`} className={day.overran ? 'mismatch' : ''}>
                      <td>{formatDate(day.date)}</td>
                      <td>
                        {day.firstAt}
                        {day.startedLate > 15 ? <small>{day.startedLate} min after it opened</small> : null}
                      </td>
                      <td>
                        {day.lastAt}
                        {day.overran ? <small>past {day.endsAt.slice(0, 5)}</small> : null}
                      </td>
                      <td className="num">{day.minutes}</td>
                      <td className="num">{day.doses}</td>
                      <td className="num">{day.staff}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </article>
      ))}

      {data?.reasons.length ? (
        <article className="card">
          <h2>Why doses were not given</h2>
          <p className="muted small">Over the last {data.days} days. The reason matters more than the count.</p>
          <ul className="list">
            {data.reasons.map((reason) => (
              <li key={reason.code}>
                <div className="row static">
                  <span className="row-main">{reason.label}</span>
                  <span className="muted small">{reason.count}</span>
                </div>
              </li>
            ))}
          </ul>
        </article>
      ) : null}
    </section>
  );
}

function describeMinutes(minutes: number): string {
  if (!minutes) return 'under a minute';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
}

function formatDate(date: string): string {
  const when = new Date(`${date}T12:00:00`);
  return when.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}
